import React, { useEffect, useState, useRef } from "react";
import { Box, TextField, MenuItem } from "@mui/material";
import VehicleDropdown from "../../components/VehicleDropdown";
import CategoryDropdown from "../../components/CategoryDropdown";
import MyDatePicker from "../../components/MyDatePicker";
import { axiosInstance } from "../../utils/commonImports";
import dayjs from "dayjs";

export default function ContractTariffPanel(props) {
  const { form, setForm, ...rest } = props;

  const {
    vehicles = [],
    vehicleGroups = [],
    clientId,
    customerId,
    selectedDuties = [],
    onTariffChange,

    // EDIT MODE PROPS
    editBillingType = null,
    editTariffId = null,
    editRows = null,
    editVehicleId = null,
    editVehicleGroupId = null,
    editFromDate = null,
    editToDate = null,
  } = props;

  // safe defaults
  const defaultRow = { limit: 0, actual: 0, extra: 0, rate: 0, total: 0 };

  const [rows, setRows] = useState({
    Days: { ...defaultRow },
    Hours: { ...defaultRow },
    Kms: { ...defaultRow },
  });

  const isEditMode =
    Boolean(editBillingType) ||
    Boolean(editTariffId) ||
    Boolean(editRows);

  const displayBillingType = isEditMode
    ? (form.billing_type !== "" ? form.billing_type : editBillingType)
    : form.billing_type;

  const showTariffDropdown =  !isEditMode ||  (form.billing_type && form.billing_type !== editBillingType);

  const [tariffList, setTariffList] = useState([]);
  const [selectedTariffId, setSelectedTariffId] = useState("");
  const invoiceLoadedRef = useRef(false);

  const displayVehicleId = (form && (form.vehicleid ?? null)) ?? editVehicleId ?? null;

  const displayVehicleNo =
    (form && (form.vehicleNo ?? "")) ||
    (displayVehicleId
      ? vehicles.find((v) => String(v.vehicle_id) === String(displayVehicleId))
          ?.vehicle_number || ""
      : "");

  const displayVehicleType =
    (form && (form.vehicleType ?? "")) ||
    (displayVehicleId
      ? vehicles.find((v) => String(v.vehicle_id) === String(displayVehicleId))
          ?.vehicle_type || ""
      : "");

  const displayVehicleGroupId =
    (form && (form.vehicleGroupId ?? null)) ?? editVehicleGroupId ?? null;

  // Fetch a label for group from vehicleGroups if parent form doesn't have name
  const displayVehicleGroupName =
    (form && (form.vehicleGroupName ?? "")) ||
    (displayVehicleGroupId
      ? vehicleGroups.find(
          (g) => Number(g.categoryid) === Number(displayVehicleGroupId)
        )?.categoryname || ""
      : "");

  const displayPeriodFrom =
    (form && (form.periodFrom || "")) || editFromDate || "";

  const displayPeriodTo = (form && (form.periodTo || "")) || editToDate || "";

  // Determine which contractRows to show: prefer form.contractRows if it has non-zero values, otherwise editRows
  const formHasRows =
    form &&
    form.contractRows &&
    (Number(form.contractRows.Days?.limit || 0) ||
      Number(form.contractRows.Hours?.limit || 0) ||
      Number(form.contractRows.Kms?.limit || 0) ||
      Number(form.contractRows.Days?.total || 0) ||
      Number(form.contractRows.Hours?.total || 0) ||
      Number(form.contractRows.Kms?.total || 0));

  const displayContractRows = formHasRows
    ? {
        Days: {
          limit: Number(form.contractRows.Days?.limit || 0),
          actual: Number(form.contractRows.Days?.actual || 0),
          extra: Number(form.contractRows.Days?.extra || 0),
          rate: Number(form.contractRows.Days?.rate || 0),
          total: Number(form.contractRows.Days?.total || 0),
        },
        Hours: {
          limit: Number(form.contractRows.Hours?.limit || 0),
          actual: Number(form.contractRows.Hours?.actual || 0),
          extra: Number(form.contractRows.Hours?.extra || 0),
          rate: Number(form.contractRows.Hours?.rate || 0),
          total: Number(form.contractRows.Hours?.total || 0),
        },
        Kms: {
          limit: Number(form.contractRows.Kms?.limit || 0),
          actual: Number(form.contractRows.Kms?.actual || 0),
          extra: Number(form.contractRows.Kms?.extra || 0),
          rate: Number(form.contractRows.Kms?.rate || 0),
          total: Number(form.contractRows.Kms?.total || 0),
        },
      }
    : editRows || rows;

  // ⭐ Ensure form.billing_type is populated on edit load
  useEffect(() => {
    if (isEditMode && !form.billing_type && editBillingType) {
      setForm((prev) => ({
        ...prev,
        billing_type: editBillingType,
      }));
    }
  }, [isEditMode, editBillingType]);

  // Ensure our internal `rows` state follows displayContractRows when necessary
  useEffect(() => {
    if (!displayContractRows) return;
    // only update internal rows when there's a meaningful change (avoid stomping user edits)
    setRows((prev) => {
      // crude deep compare for key totals — if equal, skip
      const prevTotal =
        Number(prev.Days.total) +
        Number(prev.Hours.total) +
        Number(prev.Kms.total);
      const newTotal =
        Number(displayContractRows.Days?.total || 0) +
        Number(displayContractRows.Hours?.total || 0) +
        Number(displayContractRows.Kms?.total || 0);
      if (prevTotal === newTotal) return prev;
      return {
        Days: { ...(displayContractRows.Days || defaultRow) },
        Hours: { ...(displayContractRows.Hours || defaultRow) },
        Kms: { ...(displayContractRows.Kms || defaultRow) },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayContractRows?.Days?.total,
    displayContractRows?.Hours?.total,
    displayContractRows?.Kms?.total,
  ]);

  // -----------------------
  // Sync rows → form (keep the parent informed)
  // -----------------------
  useEffect(() => {
    if (setForm) {
      setForm((prev) => ({
        ...prev,
        contractRows: rows,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // -----------------------
  // Fetch tariff list when customer, group or billing type change (use display values)
  // -----------------------
  const fetchTariffList = async () => {
    const cust = (form && form.customerid) ?? customerId ?? null;
    const vg = displayVehicleGroupId;
    const bt = displayBillingType;

    if (!cust || !vg || !bt) return;

    try {
      const res = await axiosInstance.get("/api/tariff-contract/list", {
        params: {
          client_id: clientId,
          customer_id: cust,
          vehicle_group_id: vg,
          billing_type: bt,
        },
      });
      setTariffList(res.data || []);
    } catch (err) {
      console.error("Tariff list error:", err);
    }
  };

  useEffect(() => {
    fetchTariffList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, displayVehicleGroupId, displayBillingType]);

  // -----------------------
  // if editTariffId provided, set selectedTariffId (whenever it changes)
  // -----------------------
  useEffect(() => {
    if (editTariffId) {
      setSelectedTariffId(editTariffId);
    }
  }, [editTariffId]);

  // ⭐ FIX: Ensure billing_type is populated in EDIT MODE
  useEffect(() => {
    if (isEditMode && editBillingType && !form.billing_type) {
      setForm((prev) => ({
        ...prev,
        billing_type: editBillingType,
      }));
    }
  }, [isEditMode, editBillingType]);

  // -----------------------
  // Apply tariff when selectedTariffId or tariffList changes
  // -----------------------
  useEffect(() => {
    if (!selectedTariffId) return;
    const found = tariffList.find(
      (t) => String(t.contract_id) === String(selectedTariffId)
    );
    if (!found) return;
    // Do not overwrite explicit editRows
    if (!(editRows && typeof editRows === "object")) {
      applyTariffToPanel(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tariffList, selectedTariffId]);

  const applyTariffToPanel = (t) => {
    if (!t) return;

    const perDayRate =
      Number(t.cust_days) > 0
        ? Number(t.cust_rate) / Number(t.cust_days)
        : Number(t.cust_rate);

    const perDayRateRounded = Number(perDayRate.toFixed(2));

    // ✔ FIX: Keep existing actual & extra
    setRows((prev) => ({
      Days: {
        ...prev.Days,
        limit: Number(t.cust_days || 0),
        rate: perDayRateRounded,
        total: Number(t.cust_rate || 0), // fixed monthly total
      },
      Hours: {
        ...prev.Hours,
        limit: Number(t.cust_hours || 0),
        rate: Number(t.cust_extra_hr_rate || 0),
      },
      Kms: {
        ...prev.Kms,
        limit: Number(t.cust_kms || 0),
        rate: Number(t.cust_extra_km_rate || 0),
      },
    }));

    // Keep period update (unchanged)
    if (setForm) {
      setForm((prev) => ({
        ...prev,
        periodFrom: t.contract_from_date || prev.periodFrom || "",
        periodTo: t.contract_to_date || prev.periodTo || "",
      }));
    }
  };

  // -----------------------
  // Update rows when duties change (uses internal rows + selectedTariffId)
  // NOTE: removed the guard that required selectedTariffId so duties always recompute.
  // -----------------------
  useEffect(() => {
    // safe default - ensure selectedDuties is an array
    const duties = Array.isArray(selectedDuties) ? selectedDuties : [];

    let usedHours = 0, usedKms = 0, usedDays = 0;

    duties.forEach((d) => {
      usedHours += Number(d.trip_charges_hours_used || 0);
      usedKms += Number(d.trip_charges_kms_used || 0);
      usedDays += Number(d.trip_charges_days_used || 0);
    });

    setRows((prev) => {
      const updated = structuredClone(prev);

      updated.Hours.actual = usedHours;
      updated.Kms.actual = usedKms;
      updated.Days.actual = usedDays;

      const hoursLimit = Number(updated.Hours.limit || 0);
      const kmsLimit = Number(updated.Kms.limit || 0);
      const daysLimit = Number(updated.Days.limit || 0);

      updated.Hours.extra = Math.max(0, usedHours - hoursLimit);
      updated.Kms.extra = Math.max(0, usedKms - kmsLimit);
      updated.Days.extra = Math.max(0, usedDays - daysLimit);

      updated.Hours.total = Number(
        (updated.Hours.extra * Number(updated.Hours.rate || 0)).toFixed(2)
      );
      updated.Kms.total = Number(
        (updated.Kms.extra * Number(updated.Kms.rate || 0)).toFixed(2)
      );

      // Keep Days.total as the configured fixed total if present; otherwise keep existing
      updated.Days.total = Number(prev.Days.total || updated.Days.total || 0);

      return updated;
    });
  }, [selectedDuties]); // run whenever duties change

  // -----------------------
  // Events
  // -----------------------
  const onVehicleChange = (vehicle) => {
    if (!vehicle) {
      setForm((prev) => ({
        ...prev,
        vehicleid: null,
        vehicleNo: "",
        vehicleType: "",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      vehicleid: vehicle.vehicle_id ?? null,
      vehicleNo: vehicle.vehicle_number ?? "",
      vehicleType: vehicle.vehicle_type ?? "",
    }));
  };

  const onVehicleGroupChange = (e) => {
    const selectedId = Number(e.target.value);
    const selectedName = e.target.label;

    setForm((prev) => ({
      ...prev,
      vehicleGroupId: selectedId,
      vehicleGroupName: selectedName,
    }));
  };

  const updateCell = (rowKey, field, value) => {
    const num = Number(value) || 0;

    setRows((prev) => {
      const updated = structuredClone(prev);

      updated[rowKey][field] = num;

      const r = updated[rowKey];
      r.total = Number(
        (Number(r.actual) + Number(r.extra * r.rate)).toFixed(2)
      );

      return updated;
    });
  };

  // -----------------------
  // Notify parent of tariff changes (same as before)
  // -----------------------
  useEffect(() => {
    if (!onTariffChange) return;

    onTariffChange({
      rows,
      vehicleid: displayVehicleId,
      vehicleNo: displayVehicleNo,
      vehicleGroupId: displayVehicleGroupId,
      vehicleGroupName: displayVehicleGroupName,
      billing_type: displayBillingType,
      periodFrom: displayPeriodFrom,
      periodTo: displayPeriodTo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rows,
    displayVehicleId,
    displayVehicleNo,
    displayVehicleGroupId,
    displayVehicleGroupName,
    displayBillingType,
    displayPeriodFrom,
    displayPeriodTo,
  ]);

  // ⭐ DYNAMIC ROW TITLE LOGIC BASED ON BILLING TYPE
const rowTitleMap = {
  Days: "Days",
  Hours: "Hours",
  Kms: "Kms",
};

// Apply mapping based on billing type
if (displayBillingType === "Monthly Km Limit Month Rate") {
  rowTitleMap.Hours = "Hours/Day";
  rowTitleMap.Kms = "Kms/Month";
}

if (displayBillingType === "Monthly Hrs & Kms Limit Month Rate") {
  rowTitleMap.Hours = "Hours/Month";
  rowTitleMap.Kms = "Kms/Month";
}

if (displayBillingType === "Daily Km Limit Month Rate") {
  rowTitleMap.Hours = "Hours/Day";
  rowTitleMap.Kms = "Kms/Day";
}

if (displayBillingType === "Daily Km Limit Daily Rate") {
  rowTitleMap.Hours = "Hours/Day";
  rowTitleMap.Kms = "Kms/Day";
}

  // -----------------------
  // RENDER
  // -----------------------
  return (
    <Box sx={{ mt: 3, overflow: "hidden" }}>
      {/* TOP INPUTS */}
      <Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ width: 240 }}>
          <VehicleDropdown
            clientId={clientId}
            vehicleNumber={displayVehicleNo || ""}
            onVehicleChange={onVehicleChange}
          />
        </Box>

        <Box sx={{ width: 340 }}>
          <CategoryDropdown
            clientId={clientId}
            categoryType="VehicleGroup"
            label="Vehicle Group"
            value={displayVehicleGroupId || ""}
            onChange={onVehicleGroupChange}
            options={vehicleGroups}
          />
        </Box>

        <MyDatePicker
          label="Period From"
          value={displayPeriodFrom ? new Date(displayPeriodFrom) : null}
          onChange={(date) => {
            const iso = date ? date.toISOString().slice(0, 10) : "";
            setForm((prev) => ({ ...prev, periodFrom: iso }));
          }}
          inputStyle={{ width: 160 }}
        />

        <MyDatePicker
          label="To"
          value={displayPeriodTo ? new Date(displayPeriodTo) : null}
          onChange={(date) => {
            const iso = date ? date.toISOString().slice(0, 10) : "";
            setForm((prev) => ({ ...prev, periodTo: iso }));
          }}
          inputStyle={{ width: 160 }}
        />
      </Box>

      {/* BILLING + TARIFF */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          alignItems: "center",
          flexWrap: "nowrap",
        }}
      >
        <TextField
          select
          size="small"
          label="Billing Type"
          value={displayBillingType || ""}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, billing_type: e.target.value }))
          }
          sx={{ width: 300 }}
        >
          <MenuItem value="">Select Billing Type</MenuItem>
          <MenuItem value="Monthly Km Limit Month Rate">
            Monthly Km Limit Month Rate
          </MenuItem>
          <MenuItem value="Monthly Hrs & Kms Limit Month Rate">
            Monthly Hrs & Kms Limit Month Rate
          </MenuItem>
          <MenuItem value="Daily Km Limit Month Rate">
            Daily Km Limit Month Rate
          </MenuItem>
          <MenuItem value="Daily Km Limit Daily Rate">
            Daily Km Limit Daily Rate
          </MenuItem>
          <MenuItem value="Kms Basis">Kms Basis</MenuItem>
          <MenuItem value="Per Person Basis">Per Person Basis</MenuItem>
        </TextField>

        {/* ⭐ SHOW OR HIDE TARIFF DROPDOWN */}
        {showTariffDropdown && (
          <TextField
            select
            size="small"
            label="Select Contract Tariff"
            value={selectedTariffId || ""}
            onChange={(e) => {
              setSelectedTariffId(e.target.value);
              const selected = tariffList.find(
                (t) => String(t.contract_id) === String(e.target.value)
              );
              if (selected) applyTariffToPanel(selected);
            }}
            sx={{ width: 550, background: "#fff" }}
          >
            <MenuItem value="">Select Contract Tariff</MenuItem>

            {tariffList.map((t) => (
              <MenuItem key={t.contract_id} value={t.contract_id}>
                {`${t.cust_days} Days | ${t.cust_hours} Hrs | ${
                  t.cust_kms
                } Kms | ₹${t.cust_rate} | ${dayjs(t.contract_from_date).format(
                  "DD/MM/YYYY"
                )} → ${dayjs(t.contract_to_date).format("DD/MM/YYYY")}`}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

     {/* TABLE */}
<Box sx={{ width: 650, border: "1px solid #b5b5b5", p: 1 }}>
  {/* HEADER */}
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "120px 90px 90px 90px 120px 110px",
      background: "#e6e6e6",
      p: "2px 4px",                    // 🔥 smaller padding
      fontWeight: "bold",
      fontSize: "14px",
    }}
  >
    <Box>Name</Box>
    <Box sx={{ textAlign: "right" }}>Limit</Box>
    <Box sx={{ textAlign: "right" }}>Actual</Box>
    <Box sx={{ textAlign: "right" }}>Extra</Box>
    <Box sx={{ textAlign: "right" }}>Rate</Box>
    <Box sx={{ textAlign: "right" }}>Total</Box>
  </Box>

  {["Days", "Hours", "Kms"].map((rowKey) => {
    const rowData = rows[rowKey] || { ...defaultRow };
    const isHoursRow = rowKey === "Hours";

    const formatNumber = (val, decimal = 2) =>
      Number(val || 0).toFixed(decimal);

    return (
      <Box
        key={rowKey}
        sx={{
          display: "grid",
          gridTemplateColumns: "120px 90px 90px 90px 120px 140px",
          p: "3px 6px",                 // 🔥 smaller row padding
          alignItems: "center",         // align middle
          rowGap: 0,
        }}
      >
        {/* <Box sx={{ pt: "2px", fontSize: "14px" }}>{rowKey}</Box> */}
        <Box sx={{ pt: "2px", fontSize: "14px" }}>
  {rowTitleMap[rowKey] || rowKey}
</Box>


        {/* LIMIT */}
        <TextField
          size="small"
          value={isHoursRow ? formatNumber(rowData.limit) : rowData.limit || 0}
          onChange={(e) => updateCell(rowKey, "limit", e.target.value)}
          sx={{
            width: 90,
            "& .MuiInputBase-root": {
              height: 30,               // 🔥 compact height
              fontSize: "14px",
            },
          }}
          InputProps={{ sx: { "& input": { textAlign: "right" } } }}
        />

        {/* ACTUAL */}
        <TextField
          size="small"
          value={isHoursRow ? formatNumber(rowData.actual) : rowData.actual || 0}
          onChange={(e) => updateCell(rowKey, "actual", e.target.value)}
          sx={{
            width: 90,
            "& .MuiInputBase-root": { height: 30, fontSize: "14px" },
          }}
          InputProps={{ sx: { "& input": { textAlign: "right" } } }}
        />

        {/* EXTRA */}
        <TextField
          size="small"
          value={isHoursRow ? formatNumber(rowData.extra) : rowData.extra || 0}
          onChange={(e) => updateCell(rowKey, "extra", e.target.value)}
          sx={{
            width: 90,
            "& .MuiInputBase-root": { height: 30 },
          }}
          InputProps={{ sx: { "& input": { textAlign: "right" } } }}
        />

        {/* RATE */}
        <TextField
          size="small"
          value={formatNumber(rowData.rate)}
          onChange={(e) => updateCell(rowKey, "rate", e.target.value)}
          sx={{
            width: 120,
            "& .MuiInputBase-root": { height: 30 },
          }}
          InputProps={{ sx: { "& input": { textAlign: "right" } } }}
        />

        {/* TOTAL */}
        <TextField
          size="small"
          value={formatNumber(rowData.total)}
          InputProps={{
            readOnly: true,
            sx: { "& input": { textAlign: "right" } },
          }}
          sx={{
            width: 120,
            background: "#f8f8f8",
            "& .MuiInputBase-root": { height: 30 },
          }}
        />
      </Box>
    );
  })}
</Box>

    </Box>
  );
}
